import React from "react";
import type { BackendResponse, ChatDelta, ChatStatus } from "../../lib/types";
import { useChat } from "../../context/ChatContext";
import { emitGlobalEvent, EVENT_QA_USED } from "../../lib/events";
import { QuestionAsker } from "./QuestionAsker";
import { getRuntimeLocale, t as tRuntime } from "../../lib/locale";

function getFinalText(data: BackendResponse): string {
  const locale = getRuntimeLocale();
  switch (data.type) {
    case "answer":
      return (data.answer ?? "") || "";
    case "result":
      return (data.recommendation?.customer_message ?? "") || "";
    case "prompt":
    case "options":
    case "ordered_options":
      return (data.question ?? "") || "";
    case "error":
    default:
      return (data.error ?? tRuntime("common.error_generic", locale)) || tRuntime("common.error_generic", locale);
  }
}

function readTestSessionId(): string | null {
  try {
    return localStorage.getItem("ww:test_session_id");
  } catch {
    return null;
  }
}

export function ProductPanelQuestionAsker({ productId }: { productId: string }) {
  const anyChat: any = useChat();
  const { askDetached } = anyChat;
  const storeId = anyChat?.client?.config?.storeId || (window as any).__WW__?.storeId;

  const abortRef = React.useRef<AbortController | null>(null);
  const [mode, setMode] = React.useState<"QUESTION" | "ANSWER">("QUESTION");
  const [question, setQuestion] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [answerText, setAnswerText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<{ title?: string; status?: string } | null>(null);

  // Reset local state when switching products (and abort any in-flight detached request).
  React.useEffect(() => {
    try { abortRef.current?.abort(); } catch {}
    abortRef.current = null;
    setMode("QUESTION");
    setQuestion("");
    setInputValue("");
    setAnswerText("");
    setLoading(false);
    setStatus(null);
  }, [productId]);

  const onNewQuestion = React.useCallback(() => {
    try { abortRef.current?.abort(); } catch {}
    abortRef.current = null;
    setMode("QUESTION");
    setQuestion("");
    setInputValue("");
    setAnswerText("");
    setLoading(false);
    setStatus(null);
  }, []);

  const ask = React.useCallback(async (q: string) => {
    const text = (q || "").trim();
    if (!text) return;
    const testSessionId = readTestSessionId() || (window as any).__WW__?.testSessionId;
    emitGlobalEvent(EVENT_QA_USED, {
      storeId,
      testSessionId,
      source: "product-panel-qa",
      metadata: productId ? { product_id: String(productId) } : undefined,
    });

    // Cancel any in-flight detached ask
    try { abortRef.current?.abort(); } catch {}
    abortRef.current = new AbortController();

    setQuestion(text);
    setAnswerText("");
    setMode("ANSWER");
    setLoading(true);
    setStatus({ title: tRuntime("common.thinking", getRuntimeLocale()), status: undefined });

    try {
      const data = await askDetached(text, "QUESTION", {
        signal: abortRef.current?.signal,
        onStatus: (s: ChatStatus) => {
          setStatus({ title: s?.title, status: s?.status });
        },
        onDelta: (d: ChatDelta) => {
          const chunk = (d?.delta as string) ?? ((d as any)?.token as string) ?? "";
          if (!chunk) return;
          setAnswerText((prev) => prev + chunk);
        },
      });

      setAnswerText((prev) => (prev && prev.trim().length > 0 ? prev : getFinalText(data)));
    } catch (err) {
      if ((err as any)?.name === "AbortError") return;
      setAnswerText(tRuntime("common.error_generic", getRuntimeLocale()));
    } finally {
      setLoading(false);
      setStatus(null);
    }
  }, [askDetached, storeId]);

  return (
    <QuestionAsker
      mode={mode}
      question={question}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      onSubmit={() => ask(inputValue)}
      loading={loading}
      status={status}
      answerMarkdown={answerText}
      onNewQuestion={onNewQuestion}
    />
  );
}


